import { create } from 'zustand';
import { ColumnState } from 'ag-grid-community';
import { MST1060_R10Dto } from './Dto_MST1060_R10';

export interface MST1060_R10Filter {
	searchText: string;
	itemGroup: string;
	itemAccount: string;
	itemCd: string;
	itemNm : string;
	brandCd: string;
}
export interface MST1060_R10Store {
	// 검색 필터
	searchText: string;
	setSearchText: (text: string) => void;
	MST1060_R10Filter: MST1060_R10Filter;
	MST1060_R10PastFilter: MST1060_R10Filter;
	setMST1060_R10Filter: (filter: MST1060_R10Filter) => void;
	setMST1060_R10PastFilter: (filter: MST1060_R10Filter) => void;

	// 바코드 목록
	barcodeList: MST1060_R10Dto[];
	setBarcodeList: (list: MST1060_R10Dto[]) => void;

	// 선택된 바코드 (단일)
	selectedBarcode: MST1060_R10Dto | null;
	setSelectedBarcode: (barcode: MST1060_R10Dto | null) => void;

	// 선택된 바코드 목록 (다중 선택용)
	selectedBarcodeList: string[];
	setSelectedBarcodeList: (list: string[]) => void;
	addSelectedBarcodeList: (barcodes: string[]) => void;
	removeSelectedBarcodeList: (barcodes?: string[]) => void;
	initSelectedBarcodeList: () => void;
	allSelectedBarcodeList: () => void;

	// 삭제된 바코드 목록
	deletedBarcodeList: string[];
	setDeletedBarcodeList: (list: string[]) => void;
	addDeletedBarcodeList: (barcodes: string[]) => void;
	removeDeletedBarcodeList: (barcodes?: string[]) => void;

	// 바코드 추가/삭제/업데이트
	insertBarcodeList: (data: MST1060_R10Dto[], index: number) => void;
	removeBarcodeList: (valueIndex: number) => void;
	updateBarcodeList: (valueIndex: number, data: MST1060_R10Dto) => void;

	// 원본 데이터 (취소 시 복구용)
	originalBarcodeList: MST1060_R10Dto[];
	setOriginalBarcodeList: (list: MST1060_R10Dto[]) => void;

	// 컬럼 상태
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	// 초기화
	initData: () => void;
}
export const defaultMST1060_R10Filter: MST1060_R10Filter = {
	searchText: '',
	itemGroup: '',
	itemAccount: '',
	itemCd: '',
	itemNm: '',
	brandCd: ''
};

export const useMST1060_R10Store = create<MST1060_R10Store>((set) => ({

	// ====== 검색 필터 ======
	MST1060_R10Filter : defaultMST1060_R10Filter,
	MST1060_R10PastFilter : defaultMST1060_R10Filter,
	setMST1060_R10Filter: (filter: MST1060_R10Filter) => set((state) => ({
		...state,
		MST1060_R10Filter: { ...state.MST1060_R10Filter, ...filter }
	})),
	setMST1060_R10PastFilter: (filter: MST1060_R10Filter) => set((state) => ({
		...state,
		MST1060_R10PastFilter: { ...state.MST1060_R10PastFilter, ...filter }
	})),

	searchText: '',
	setSearchText: (text) => set({ searchText: text }),

	barcodeList: [],
	setBarcodeList: (list) => set({ barcodeList: list }),

	selectedBarcode: null,
	setSelectedBarcode: (barcode) => set({ selectedBarcode: barcode }),

	selectedBarcodeList: [],
	setSelectedBarcodeList: (list) => set({ selectedBarcodeList: list }),
	addSelectedBarcodeList: (barcodes) => set((state) => ({
		selectedBarcodeList: [...state.selectedBarcodeList, ...barcodes]
	})),
	removeSelectedBarcodeList: (barcodes) => set((state) => ({
		selectedBarcodeList: barcodes 
			? state.selectedBarcodeList.filter(b => !barcodes.includes(b))
			: []
	})),
	initSelectedBarcodeList: () => set({ selectedBarcodeList: [] }),
	allSelectedBarcodeList: () => set((state) => ({
		selectedBarcodeList: state.barcodeList
			.filter(item => !state.deletedBarcodeList.includes(item.barcode))
			.map(item => item.valueIndex.toString())
	})),

	deletedBarcodeList: [],
	setDeletedBarcodeList: (list) => set({ deletedBarcodeList: list }),
	addDeletedBarcodeList: (barcodes) => set((state) => ({
		deletedBarcodeList: [...state.deletedBarcodeList, ...barcodes]
	})),
	removeDeletedBarcodeList: (barcodes) => set((state) => ({
		deletedBarcodeList: barcodes
			? state.deletedBarcodeList.filter(b => !barcodes.includes(b))
			: []
	})),

	insertBarcodeList: (data, afterIndex) => set((state) => {
		const list = [...state.barcodeList];
		
		// 배열 인덱스에 직접 삽입
		if (afterIndex === -1) {
			// 맨 앞에 추가
			list.unshift(...data);
		} else {
			// 특정 위치 다음에 추가
			list.splice(afterIndex + 1, 0, ...data);
		}
		
		// valueIndex 재정렬 (표시용)
		const reindexedList = list.map((item, idx) => ({
			...item,
			valueIndex: idx,
			orderIndex: idx
		}));
		return { barcodeList: reindexedList };
	}),

	removeBarcodeList: (index) => set((state) => ({
		barcodeList: state.barcodeList.filter((_, i) => i !== index)
	})),

	updateBarcodeList: (index, data) => set((state) => {
		const list = [...state.barcodeList];
		if (index >= 0 && index < list.length) {
			list[index] = { ...list[index], ...data };
		}
		return { barcodeList: list };
	}),

	originalBarcodeList: [],
	setOriginalBarcodeList: (list) => set({ originalBarcodeList: list }),

	columnState: null,
	setColumnState: (state) => set({ columnState: state }),

	scrollTop: null,
	setScrollTop: (top: number | null) => set({ scrollTop: top }),

	initData: () => set({
		MST1060_R10Filter : defaultMST1060_R10Filter,
		MST1060_R10PastFilter : defaultMST1060_R10Filter,
		searchText: '',
		barcodeList: [],
		selectedBarcode: null,
		selectedBarcodeList: [],
		deletedBarcodeList: [],
		originalBarcodeList: [],
		columnState: null,
		scrollTop: null,
	})
}));

